"use client";
import React, { useEffect, useState } from "react";
import "@/styles/components/privacy_policy.scss";

import Navbar from "@/components/common/Navbar";
import Footer from "@/components/common/Footer";
import Image from "next/image";

const privacyData = [
  {
    id: "information-collection",
    title: "Information We Collect",
    items: [
      {
        id: "personal-information",
        title: "Personal Information",
        content: [
          "Information that identifies you directly or indirectly, including:",
          [
            "Full name",
            "Email address",
            "Phone number",
            "Billing and shipping address",
            "Date of birth",
            "Account login credentials",
            "Order history and transaction records",
            "Customer support communications",
          ],
          "We do not store full credit card numbers. Payments are processed by secure third-party payment providers.",
        ],
      },
      {
        id: "device-information",
        title: "Mobile App & Device Information",
        content: [
          "When you use the Baskitt App, we may collect:",
          [
            "Device type and unique identifiers",
            "Operating system and version",
            "IP address",
            "App usage behavior",
            "GPS/location data (where permitted and enabled by you)",
          ],
          "Location data may be used to improve delivery services, provide localized offers, and enhance user experience.",
          "You may disable location access at any time via your device settings.",
        ],
      },
      {
        id: "technical-information",
        title: "Technical & Usage Information",
        content: [
          "We automatically collect certain technical information, including:",
          [
            "Browser type",
            "Pages viewed",
            "Date and time of visits",
            "Referring URLs",
            "Cookies and tracking technologies",
          ],
          "Cookies help us improve functionality, personalize content, and analyze performance.",
          "You may disable cookies in your browser settings, though some features may not function properly.",
        ],
      },
    ],
  },

  {
    id: "information-usage",
    title: "How We Use Your Information",
    items: [
      {
        id: "usage-details",
        title: "Use of Information",
        content: [
          "We use your information to:",
          [
            "Create and manage user accounts",
            "Process orders and payments",
            "Facilitate deliveries",
            "Provide customer support",
            "Improve app performance and features",
            "Detect fraud and protect security",
            "Send service-related communications",
            "Comply with legal obligations",
            "Send promotional communications (you may opt out at any time)",
          ],
          "We may generate anonymized and aggregated data for analytics and business improvement.",
        ],
      },
    ],
  },

  {
    id: "sharing",
    title: "Sharing & Disclosure",
    items: [
      {
        id: "sharing-details",
        title: "Information Sharing",
        content: [
          "We may share your information with:",
          [
            "Retailers and sellers to fulfill orders",
            "Payment processors and financial institutions",
            "Delivery and logistics partners",
            "Hosting and technical service providers",
            "Marketing and analytics providers",
            "Government or legal authorities when required by law",
          ],
          "We do not sell your personal data.",
          "Third-party service providers are contractually required to safeguard your information.",
        ],
      },
    ],
  },

  {
    id: "data-retention",
    title: "Data Retention",
    items: [
      {
        id: "retention-details",
        title: "Data Retention",
        content: [
          "We retain personal data only as long as necessary to:",
          [
            "Provide Services",
            "Complete transactions",
            "Comply with legal, tax, or regulatory requirements",
            "Resolve disputes",
            "Enforce agreements",
          ],
          "Deleted data may remain in backup systems for a limited period.",
        ],
      },
    ],
  },

  {
    id: "user-rights",
    title: "Your Rights & Account Deletion",
    items: [
      {
        id: "rights-details",
        title: "User Rights",
        content: [
          "You may:",
          [
            "Access your personal information",
            "Update or correct inaccurate data",
            "Withdraw consent where applicable",
            "Request account deletion",
            "Opt out of marketing communications",
          ],
          "For detailed instructions on how to request account deletion, please visit our Account Deletion Request page.",
          "We may retain certain information as required by law, fraud prevention, dispute resolution, tax, or regulatory obligations.",
        ],
      },
    ],
  },

  {
    id: "security",
    title: "Security",
    items: [
      {
        id: "security-measures",
        title: "Security Measures",
        content: [
          "We implement industry-standard security measures including:",
          [
            "Encrypted connections (HTTPS)",
            "Secure servers",
            "Access controls",
            "Authentication procedures",
          ],
          "However, no system is completely secure. You are responsible for protecting your account credentials.",
        ],
      },
    ],
  },

  {
    id: "third-party",
    title: "Third-Party Links",
    items: [
      {
        id: "third-party-links",
        title: "Third-Party Services",
        content: [
          "Baskitt may contain links to third-party websites or services.",
          "Once you leave our platform, their privacy policies apply.",
          "We are not responsible for their data practices.",
        ],
      },
    ],
  },

  {
    id: "children",
    title: "Children's Privacy",
    items: [
      {
        id: "children-privacy",
        title: "Children's Privacy",
        content: [
          "Baskitt is not intended for individuals under the age of 18 without parental or guardian consent.",
          "We do not knowingly collect personal data from minors.",
          "If we become aware of such data, we will take reasonable steps to delete it.",
        ],
      },
    ],
  },

  {
    id: "policy-changes",
    title: "Changes to This Privacy Policy",
    items: [
      {
        id: "policy-updates",
        title: "Policy Updates",
        content: [
          "We may update this Privacy Policy at any time.",
          "Changes become effective immediately upon posting.",
          "Continued use of the Services constitutes acceptance of the updated policy.",
        ],
      },
    ],
  },

  {
    id: "contact",
    title: "Contact Us",
    items: [
      {
        id: "contact-information",
        title: "Contact Information",
        content: [
          "If you have questions about this Privacy Policy or your personal data:",
          ["Baskitt App"],
          "Email: website@baskitt.co",
          "Website: www.baskitt.co",
        ],
      },
    ],
  },
];

export default function PrivacyPolicy() {
  const [active, setActive] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;

      privacyData.forEach((section) => {
        const firstItem = document.getElementById(section.items[0].id);
        const lastItem = document.getElementById(
          section.items[section.items.length - 1].id,
        );

        if (!firstItem || !lastItem) return;

        const top = firstItem.offsetTop;
        const bottom = lastItem.offsetTop + lastItem.offsetHeight;

        if (scrollPosition >= top && scrollPosition < bottom) {
          setActive(section.id);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const renderContent = (content) => {
    if (typeof content === "string") return <p>{content}</p>;

    if (Array.isArray(content)) {
      return content.map((item, index) => {
        if (Array.isArray(item)) {
          return (
            <ul key={index}>
              {item.map((li, i) => (
                <li key={i}>{li}</li>
              ))}
            </ul>
          );
        }
        return <p key={index}>{item}</p>;
      });
    }
  };

  return (
    <>
      <div className="section navbar_section">
        <Navbar />
      </div>

      {/* HEADER */}
      <section id="policy_header">
        <h1>
          Privacy <span>Policy</span>
        </h1>

        <div className="box_image">
          <Image
            src="/images/terms.png"
            className="terms"
            width={110}
            height={110}
            alt="policy"
          />
        </div>
      </section>

      {/* POLICY CONTENT */}
      <section id="policy_section">
        <div className="policy_wrapper">
          {/* SIDEBAR */}
          <div className="policy_sidebar">
            <ul>
              {privacyData.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.items[0].id}`}
                    className={active === section.id ? "active" : ""}
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* CONTENT */}
          <div className="policy_content">
            {privacyData.map((section, sectionIndex) => (
              <div key={section.id}>
                <h2>
                  {sectionIndex + 1}. {section.title}
                </h2>

                {section.items.map((item, itemIndex) => (
                  <section key={item.id} id={item.id}>
                    <h3>
                      {sectionIndex + 1}.{itemIndex + 1} {item.title}
                    </h3>

                    {renderContent(item.content)}
                  </section>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
